/**
 * Bridge for the process-wide v2 transcription config (`set_transcription_config`).
 *
 * APP-3779: Rust-initiated upload paths — currently the chunked bulk-sync
 * engine (`session_creation_engine::process_chunked_task`) — need to know
 * whether the v2 EKS service is enabled so they can route chunks to the
 * right backend. Live sessions already carry their config via
 * `chronicle_session_start` params; this bridge covers the gaps where
 * bulk-sync runs without a live phase having pushed config first (app
 * startup, flag flip between recordings).
 *
 * The Rust IPC stores config in `TranscriptionConfigState` (process-wide
 * `RwLock<TranscriptionServiceConfig>`). See
 * `src-tauri/src/session/transcription_config_state.rs`.
 */
import { isTauri } from '@/tauri/utils';

export interface SetTranscriptionConfigParams {
  /** Mirrors `useIsAppsUseTranscriptionServiceV2DesktopEnabled`. */
  useTranscriptionServiceV2: boolean;
  /**
   * EKS transcription base URL (e.g.
   * `https://api.aws-aue1.heidihealth.com/transcription/main/v1`). Pass
   * `null` for v1. The Rust side treats `""` the same as `null` — same
   * defensive shape as `chronicle_session_start`.
   */
  transcriptionBaseUrl: string | null;
  /**
   * BCP-47 language codes for the v2 register PUT body. Empty acceptable.
   * Ignored on v1.
   */
  languages: string[];
}

/**
 * Push the current v2 transcription config to Rust state. Fire-and-forget —
 * Rust returns `Result<(), String>` and a write can't realistically fail
 * (it's a `RwLock::write` over an in-process value). Callers should still
 * `.catch()` so the unhandled rejection doesn't pollute logs in test
 * environments.
 *
 * Idempotent: repeated calls with the same payload are no-ops on the Rust
 * side. Safe to invoke on every render of a hook that reads the flag.
 */
export async function setTranscriptionConfig(
  params: SetTranscriptionConfigParams
): Promise<void> {
  if (!isTauri()) return;
  const { invoke } = await import('@tauri-apps/api/core');
  await invoke<void>('set_transcription_config', { params });
}
